import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight, Music } from 'lucide-react';
import Navbar from '../components/Navbar';
import MascotCharacter from '../components/MascotCharacter';
import StudioPromo from '../components/StudioPromo';
import { base44 } from '../api/base44Client';

const recentWork = [
  { title: 'A Levels Tutor — CS, Physics & Maths', tag: 'Present', path: '/projects' },
  { title: 'SAT Coach', tag: 'Present', path: '/projects' },
  { title: 'Registrations — SPADES', tag: 'Present', path: '/projects' },
];

export default function Home() {
  const [showAnswer, setShowAnswer] = useState(false);
  const [answerText, setAnswerText] = useState('');
  const [answerSubmitted, setAnswerSubmitted] = useState(false);

  const handleAnswerSubmit = async (e) => {
    e.preventDefault();
    if (!answerText.trim()) return;
    await base44.entities.Answer.create({ content: answerText });
    setAnswerText('');
    setAnswerSubmitted(true);
    setTimeout(() => setAnswerSubmitted(false), 3000);
  };

  return (
    <div className="min-h-screen bg-background dot-bg relative overflow-hidden">

      <Navbar />
      <StudioPromo />

      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/8 rounded-full blur-[120px] pointer-events-none -translate-y-1/4 translate-x-1/4" />

      <div className="max-w-5xl mx-auto px-8 pt-36 pb-20">

        {/* Top split layout */}
        <div className="grid md:grid-cols-2 gap-16 items-start mb-24">

          {/* Left: Identity */}
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/30 text-primary text-xs font-semibold px-3 py-1 rounded-full font-mono-cv mb-6">
              <span className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse" />
              chilling rn
            </div>
            <h1 className="text-5xl md:text-6xl font-extrabold text-foreground leading-[1.05] tracking-tight mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>
              mbk.<br />
              <span style={{ color: 'hsl(var(--primary))' }}>Universe</span><br />
              (maybe a khan)
            </h1>
            <p className="text-muted-foreground text-sm leading-relaxed max-w-sm">
              i do what i feel like, and somehow the rest figures itself out... basically driven by mood, curiosity, and ideas that are a little too clingy (btw ... is my trademark)
            </p>
            <div className="flex gap-3 mt-8">
              <Link to="/about" className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-bold text-sm px-5 py-2.5 rounded-lg hover:opacity-90 transition-all hover:shadow-lg hover:shadow-primary/20">
                About Me <ArrowRight size={14} />
              </Link>
              <Link to="/current-lore" className="inline-flex items-center gap-2 border border-border text-muted-foreground text-sm font-semibold px-5 py-2.5 rounded-lg hover:border-primary/50 hover:text-foreground transition-all">
                Current Lore
              </Link>
            </div>
          </motion.div>

          {/* Right: Info cards */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-col gap-4 pt-4">
            <div className="bg-card border border-border rounded-2xl p-5">
              <p className="text-xs text-muted-foreground font-mono-cv mb-1">CURRENTLY</p>
              <p className="font-bold text-foreground text-sm">Lahore University of Management Sciences (LUMS)</p>
              <p className="text-xs text-primary mt-1 font-mono-cv">2025 - Present</p>
            </div>
            <div className="bg-card border border-border rounded-2xl p-5">
              <p className="text-xs text-muted-foreground font-mono-cv mb-1">INTERESTS</p>
              <div className="relative h-36 mt-2 overflow-hidden w-full">
                {[
                  { label: 'C++',                sx: 2,  sy: 5,  ex: 55, ey: 65 },
                  { label: 'C',                  sx: 70, sy: 5,  ex: 2,  ey: 65 },
                  { label: 'Visual Basic',        sx: 20, sy: 65, ex: 55, ey: 5  },
                  { label: 'Theoretical Physics', sx: 35, sy: 35, ex: 2,  ey: 5  },
                  { label: 'Problem Solving',     sx: 2,  sy: 50, ex: 45, ey: 75 },
                  { label: 'Robotics',            sx: 55, sy: 70, ex: 15, ey: 10 },
                  { label: 'Success 🙃',          sx: 30, sy: 5,  ex: 60, ey: 70 },
                  { label: 'Running',              sx: 50, sy: 45, ex: 5,  ey: 80 },
                  { label: 'Philosophical Fiction', sx: 10, sy: 80, ex: 65, ey: 30 },
                  { label: 'Yapping...',             sx: 40, sy: 20, ex: 10, ey: 60 },
                  { label: 'Cycling',                sx: 60, sy: 55, ex: 30, ey: 15 },
                  { label: 'Reading Poetry',         sx: 15, sy: 25, ex: 70, ey: 80 },

                ].map(({ label, sx, sy, ex, ey, audioUrl }, i) => (
                  <motion.div
                    key={label}
                    className="absolute font-mono-cv text-xs text-primary/80 bg-primary/5 border border-primary/20 px-2 py-0.5 rounded-full cursor-default whitespace-nowrap flex items-center gap-1"
                    style={{ left: `${sx}%`, top: `${sy}%` }}
                    animate={{
                      left: [`${sx}%`, `${ex}%`, `${sx}%`],
                      top:  [`${sy}%`, `${ey}%`, `${sy}%`],
                    }}
                    whileHover={{ scale: 1.08, opacity: 1 }}
                    transition={{ duration: 18 + i * 2.2, repeat: Infinity, ease: 'linear' }}
                  >
                    {label}
                    {audioUrl && (
                      <a href={audioUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-3 h-3 hover:text-primary transition-colors" onClick={e => e.stopPropagation()}>
                        <Music size={10} />
                      </a>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
            <Link to="/journal" className="bg-card border border-primary/20 rounded-2xl p-5 flex items-center justify-between group hover:border-primary/50 transition-all relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent pointer-events-none" />
              <div>
                <p className="text-xs text-primary/60 font-mono-cv mb-2 tracking-widest">WEEK'S QUOTE</p>
                <p className="text-primary text-sm leading-relaxed" style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', letterSpacing: '0.01em', textShadow: '0 0 20px rgba(139,92,246,0.3)' }}>"He who has a why to live can bear almost any how."</p>
                <p className="text-primary/50 text-xs mt-1.5 font-mono-cv tracking-wide">— Nietzsche</p>
              </div>
              <ArrowUpRight size={16} className="text-border group-hover:text-primary transition-colors flex-shrink-0" />
            </Link>
          </motion.div>
        </div>

        {/* Recent projects strip */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }}>
          <div className="flex items-center justify-between mb-6">
            <p className="font-mono-cv text-xs text-muted-foreground tracking-widest uppercase">Recent Experience</p>
            <Link to="/projects" className="font-mono-cv text-xs text-primary hover:underline flex items-center gap-1">
              All projects <ArrowRight size={12} />
            </Link>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {recentWork.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + i * 0.1 }}
              >
                <Link to={item.path} className="block bg-card border border-border rounded-xl p-5 hover:border-primary/40 transition-all group">
                  <div className="flex items-start justify-between mb-3">
                    <span className="font-mono-cv text-xs text-primary bg-primary/10 border border-primary/20 px-2 py-0.5 rounded-full">{item.tag}</span>
                    <ArrowUpRight size={14} className="text-border group-hover:text-primary transition-colors" />
                  </div>
                  <p className="font-extrabold text-foreground text-sm group-hover:text-primary transition-colors" style={{ fontFamily: 'Syne, sans-serif' }}>{item.title}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Contact strip */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-16 border-t border-border pt-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div className="flex flex-col gap-4">
            <p className="text-muted-foreground text-sm">wanna contact me? <span className="text-primary font-semibold">reach out</span></p>
            <div className="flex flex-col sm:flex-row items-start gap-3">
              <a href="mailto:29100076@lums.edu.pk" className="font-mono-cv text-xs text-primary border border-primary/30 bg-primary/5 px-4 py-2 rounded-lg hover:bg-primary/10 transition-all flex items-center gap-2">
                29100076@lums.edu.pk
              </a>
              <a href="tel:+923033386333" className="font-mono-cv text-xs text-primary border border-primary/30 bg-primary/5 px-4 py-2 rounded-lg hover:bg-primary/10 transition-all flex items-center gap-2">
                +92-303-3386333
              </a>
              <a href="https://www.linkedin.com/in/mbk518/" target="_blank" rel="noopener noreferrer" className="font-mono-cv text-xs text-primary border border-primary/30 bg-primary/5 px-4 py-2 rounded-lg hover:bg-primary/10 transition-all flex items-center gap-2">
                LinkedIn
              </a>
              <a href="https://www.instagram.com/bilalkashifkhan/" target="_blank" rel="noopener noreferrer" className="font-mono-cv text-xs text-primary border border-primary/30 bg-primary/5 px-4 py-2 rounded-lg hover:bg-primary/10 transition-all flex items-center gap-2">
                Instagram
              </a>
            </div>
          </div>



          {/* Mascot */}
          <div className="flex items-end justify-center flex-shrink-0">
            <MascotCharacter />
          </div>

          {/* Share form on right */}
          <div className="flex-shrink-0 pt-12">
            <div className="space-y-3">
              {!showAnswer ? (
                <button
                  onClick={() => setShowAnswer(true)}
                  className="w-full md:w-auto font-mono-cv text-xs text-primary border border-primary/30 bg-primary/5 px-4 py-2 rounded-lg hover:bg-primary/10 transition-all whitespace-nowrap"
                >
                  share something?
                </button>
              ) : (
                <form onSubmit={handleAnswerSubmit} className="bg-card border border-primary/20 rounded-xl p-4 w-full md:w-80">
                  <textarea
                    value={answerText}
                    onChange={e => setAnswerText(e.target.value)}
                    placeholder="drop ur thoughts..."
                    maxLength={500}
                    className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground font-mono-cv outline-none focus:border-primary/60 transition-all resize-none h-24 mb-2"
                  />
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={!answerText.trim()}
                      className="flex-1 bg-primary text-primary-foreground font-bold font-mono-cv text-xs py-2 rounded-lg hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      send
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowAnswer(false); setAnswerText(''); }}
                      className="flex-1 border border-border text-muted-foreground font-mono-cv text-xs py-2 rounded-lg hover:border-primary/40 transition-all"
                    >
                      cancel
                    </button>
                  </div>
                </form>
              )}
              {answerSubmitted && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="bg-card/90 backdrop-blur-xl border border-primary/40 rounded-lg px-4 py-3 text-center"
                >
                  <p className="font-mono-cv text-xs text-primary">got it ✓</p>
                </motion.div>
              )}
            </div>
          </div>
        </motion.div>

      </div>
    <div className="fixed bottom-6 right-8 pointer-events-none select-none" style={{ transform: 'rotate(-12deg)' }}>
        <span className="text-primary/25 text-5xl" style={{ fontFamily: 'Dancing Script, cursive' }}>mbk</span>
      </div>
    <div className="text-center py-4 border-t border-border mt-4">
      <p className="font-mono-cv text-xs text-muted-foreground/40">all rights reserved by mbk</p>
    </div>
    </div>
  );
}